/**
 * The legacy authorization surface, kept alive as the ADR-110 fork seam.
 *
 * Almost everything exported here is `@deprecated`, and the tags are the point
 * — this module answered every permission question before ADR-092, so it is
 * still what autocomplete offers and what a nearby file imports from out of
 * habit. Three kinds of thing live here now:
 *
 *   - **Role bags and permission maths** (`teamRoleHasPermission`,
 *     `getTeamRolePermissions`, `hasPermissionWithHierarchy`,
 *     `EXTERNAL_MEMBER_PERMISSIONS`, `canView` and friends). These are second
 *     copies of what `@langwatch/authz` owns. They agree today because
 *     `roles-parity.unit.test.ts` holds them cell-for-cell against the
 *     engine's, which is a guard, not a reason to read them.
 *   - **The `.use()`d tRPC middlewares**, replaced by declaring
 *     `.permission("…")` on the procedure (ADR-092 decision 25).
 *   - **The fork resolvers** (`resolveProjectPermission`,
 *     `hasOrganizationPermission`, the `batch*` helpers). These still RUN:
 *     they ask the engine for a migrated organization and walk the legacy
 *     tables for one that is not. They are not dead code, they are the thing
 *     the contract PR deletes.
 *
 * What is NOT deprecated: `authorizeInResolver`, the ops-scope surface, the
 * demo-project predicates and `organizationDenialReason` — those are current.
 *
 * New code decides through `getApp().permissions`, or the `probe*Permission` /
 * `require*Permission` helpers in `~/server/app-layer/permissions/imperative`.
 */
import type {
  AuthzDenialReason,
  AuthzPermission,
  EnforcedScopeFields,
} from "@langwatch/authz";
import { declareAuthzMiddleware } from "@langwatch/authz";
import { TRPCError } from "@trpc/server";
import { env } from "~/env.mjs";
import {
  OrganizationUserRole,
  type PrismaClient,
  RoleBindingScopeType,
  TeamUserRole,
} from "~/generated/prisma/client";
import { authzChecksFor } from "~/server/app-layer/authz/checks";
import { organizationOnAuthzEngine } from "~/server/app-layer/authz/engine-gate";
import {
  LiteMemberRestrictedError,
  MembershipDisabledError,
  ProjectPermissionDeniedError,
} from "~/server/app-layer/permissions/errors";
import type { Session } from "~/server/auth";
import { type Resource, Resources } from "~/utils/rbacVocabulary";
import { isAdmin } from "../../../ee/admin/isAdmin";
import { CUSTOM_ROLE_KIND } from "../role/role-kind";

// ============================================================================
// PERMISSION DEFINITIONS
// ============================================================================

/**
 * ADR-092 decision 25: the legacy cross-product type is retired — Permission
 * IS the registry vocabulary now. Sites that legitimately handle unregistered
 * legacy custom-role strings do so as plain strings, never as Permission.
 *
 * @deprecated Import `AuthzPermission` from `@langwatch/authz` instead. This
 * is a bare alias of it kept so the legacy call sites still read, and it puts
 * the permission vocabulary behind an rbac import that nothing else about a
 * new call site needs.
 */
export type Permission = AuthzPermission;

/**
 * Resources that only exist at the organization tier — there is no team- or
 * project-scoped meaning for them (the AI Governance family plus the
 * organization resource itself). Org-tier authority comes only from an
 * ORGANIZATION-scoped RoleBinding; a TEAM- or PROJECT-scoped binding must
 * never grant a permission on one of these, even via a custom role that lists
 * it. This is the defense the scope-chain resolvers apply so a custom role
 * misconfigured below the org tier can't escalate to organization:manage,
 * governance:manage, anomalyRules:manage, and so on (ADR-021).
 *
 * Gateway + core resources (virtualKeys, gatewayBudgets, datasets, workflows,
 * …) are deliberately NOT here: they are legitimately accessible at team and
 * project scope, so team/project bindings may grant them.
 */
const ORG_EXCLUSIVE_RESOURCES: ReadonlySet<Resource> = new Set<Resource>([
  Resources.ORGANIZATION,
  Resources.GOVERNANCE,
  Resources.INGESTION_SOURCES,
  Resources.ANOMALY_RULES,
  Resources.COMPLIANCE_EXPORT,
  Resources.ACTIVITY_MONITOR,
  Resources.AI_TOOLS,
  Resources.WEBHOOK_ENDPOINTS,
  Resources.GATEWAY_SPEND,
  Resources.GOVERNANCE_COST,
]);

/**
 * True when the permission targets an organization-tier-only resource.
 *
 * @deprecated Use `permissionGrantTiers` from `@langwatch/authz` instead —
 * the registry records which tiers each permission is grantable at, so it
 * cannot fall behind a new resource the way this hand-kept set can.
 */
export function isOrgExclusivePermission(permission: Permission): boolean {
  const resource = permission.split(":")[0] as Resource;
  return ORG_EXCLUSIVE_RESOURCES.has(resource);
}

/**
 * Whether a binding at `scopeType` may grant `permission`. Org-exclusive
 * permissions require an ORGANIZATION-scoped binding; everything else is
 * grantable at any scope. Both the tRPC resolver (`checkPermissionFromBindings`)
 * and the gateway resolver (`checkRoleBindingPermission`) gate on this so the
 * rule holds no matter which path evaluates the binding.
 *
 * @deprecated Use `bindingScopeCanGrantPermission` from `@langwatch/authz`
 * instead — the ADR-021 fence is the engine's, and this is a second copy of
 * it standing in front of the legacy walk.
 */
export function bindingScopeCanGrant(
  scopeType: RoleBindingScopeType,
  permission: Permission,
): boolean {
  if (scopeType === RoleBindingScopeType.ORGANIZATION) return true;
  return !isOrgExclusivePermission(permission);
}

// ============================================================================
// ROLE DEFINITIONS
// ============================================================================

/**
 * Define what permissions each team role has
 * This is the single source of truth for team permissions
 */
const TEAM_ROLE_PERMISSIONS: Record<TeamUserRole, Permission[]> = {
  [TeamUserRole.ADMIN]: [
    // Projects
    "project:view",
    "project:create",
    "project:update",
    "project:delete",
    "project:manage",
    // Analytics
    "analytics:view",
    "analytics:manage",
    // The product around a dashboard (navigation, dashboard tools)
    "navigation:view",
    // Cost
    "cost:view",
    // Traces
    "traces:view",
    "traces:create",
    "traces:update",
    "traces:share",
    // Annotations
    "annotations:view",
    "annotations:manage",
    // Evaluations
    "evaluations:view",
    "evaluations:manage",
    // Tasks (AI Debugger)
    "tasks:view",
    "tasks:manage",
    // QA Bench policy (admins publish it)
    "qaPolicy:view",
    "qaPolicy:manage",
    // Langy (manage implies create/update/delete via the hierarchy rule)
    "langy:view",
    "langy:manage",
    // Workflows
    "workflows:view",
    "workflows:manage",
    // Experiments
    "experiments:view",
    "experiments:manage",
    // Datasets
    "datasets:view",
    "datasets:manage",
    // Triggers
    "triggers:view",
    "triggers:manage",
    // Prompts
    "prompts:view",
    "prompts:manage",
    // Scenarios
    "scenarios:view",
    "scenarios:manage",
    // Secrets
    "secrets:view",
    "secrets:manage",
    // Agent cache
    "agentCache:view",
    "agentCache:manage",
    // Team
    "team:view",
    "team:manage",
    // AI Gateway (admin has full gateway CRUD + rotation)
    "virtualKeys:view",
    "virtualKeys:create",
    "virtualKeys:update",
    "virtualKeys:delete",
    "virtualKeys:rotate",
    "virtualKeys:manage",
    // Off-boarding sweep capability — view personal VKs owned by OTHER
    // users in the org (own personal-VK visibility stays implicit on
    // principalUserId match, no perm needed). Spec-bound to
    // vk-scope-rbac.feature + vk-personal-scope.feature.
    "virtualKeys:viewOtherPersonal",
    "gatewayBudgets:view",
    "gatewayBudgets:create",
    "gatewayBudgets:update",
    "gatewayBudgets:delete",
    "gatewayBudgets:manage",
    "gatewayProviders:view",
    "gatewayProviders:update",
    "gatewayProviders:manage",
    "routingPolicies:view",
    "routingPolicies:manage",
    "gatewayGuardrails:view",
    "gatewayGuardrails:attach",
    "gatewayGuardrails:detach",
    "gatewayGuardrails:manage",
    "gatewayLogs:view",
    "auditLog:view",
    "gatewayUsage:view",
    "gatewayCacheRules:view",
    "gatewayCacheRules:create",
    "gatewayCacheRules:update",
    "gatewayCacheRules:delete",
    "gatewayCacheRules:manage",
  ],
  [TeamUserRole.MEMBER]: [
    // Projects
    "project:view",
    "project:create",
    "project:update",
    // Analytics
    "analytics:view",
    "analytics:manage",
    // The product around a dashboard (navigation, dashboard tools)
    "navigation:view",
    // Cost
    "cost:view",
    // Traces
    "traces:view",
    "traces:create",
    "traces:update",
    "traces:share",
    // Annotations
    "annotations:view",
    "annotations:manage",
    // Evaluations
    "evaluations:view",
    "evaluations:manage",
    // Tasks (AI Debugger)
    "tasks:view",
    "tasks:manage",
    // QA Bench policy (members read it; admins publish it)
    "qaPolicy:view",
    // Langy — may run the assistant, not administer it
    "langy:view",
    "langy:create",
    "langy:update",
    "langy:delete",
    // Workflows
    "workflows:view",
    "workflows:manage",
    // Experiments
    "experiments:view",
    "experiments:manage",
    // Datasets
    "datasets:view",
    "datasets:manage",
    // Triggers
    "triggers:view",
    "triggers:manage",
    // Prompts
    "prompts:view",
    "prompts:manage",
    // Scenarios
    "scenarios:view",
    "scenarios:manage",
    // Secrets
    "secrets:view",
    "secrets:manage",
    // Agent cache
    "agentCache:view",
    "agentCache:manage",
    // Team
    "team:view",
    // AI Gateway (member: can manage own VKs + see budgets, cannot delete budgets)
    "virtualKeys:view",
    "virtualKeys:create",
    "virtualKeys:update",
    "virtualKeys:rotate",
    "gatewayBudgets:view",
    "gatewayProviders:view",
    "routingPolicies:view",
    "gatewayGuardrails:view",
    "gatewayLogs:view",
    "auditLog:view",
    "gatewayUsage:view",
    "gatewayCacheRules:view",
  ],
  [TeamUserRole.VIEWER]: [
    // Projects
    "project:view",
    // Analytics
    "analytics:view",
    // The product around a dashboard (navigation, dashboard tools)
    "navigation:view",
    // Traces
    "traces:view",
    // Annotations
    "annotations:view",
    // Evaluations
    "evaluations:view",
    // Tasks (AI Debugger)
    "tasks:view",
    // QA Bench policy
    "qaPolicy:view",
    // Datasets
    "datasets:view",
    // Workflows
    "workflows:view",
    // Experiments
    "experiments:view",
    // Prompts
    "prompts:view",
    // Scenarios
    "scenarios:view",
    // Secrets
    "secrets:view",
    // Team
    "team:view",
    // AI Gateway (viewer: read-only)
    "virtualKeys:view",
    "gatewayBudgets:view",
    "gatewayProviders:view",
    "routingPolicies:view",
    "gatewayGuardrails:view",
    "gatewayLogs:view",
    "auditLog:view",
    "gatewayUsage:view",
    "gatewayCacheRules:view",
  ],
  [TeamUserRole.CUSTOM]: [
    // CUSTOM role permissions fall back to VIEWER if no assignedRoleId or custom role has no permissions
    // Projects
    "project:view",
    // Analytics
    "analytics:view",
    // The product around a dashboard (navigation, dashboard tools)
    "navigation:view",
    // Traces
    "traces:view",
    // Annotations
    "annotations:view",
    // Evaluations
    "evaluations:view",
    // Tasks (AI Debugger)
    "tasks:view",
    // QA Bench policy
    "qaPolicy:view",
    // Datasets
    "datasets:view",
    // Workflows
    "workflows:view",
    // Experiments
    "experiments:view",
    // Prompts
    "prompts:view",
    // Scenarios
    "scenarios:view",
    // Secrets
    "secrets:view",
    // Team
    "team:view",
    // AI Gateway (custom role default: same baseline as VIEWER, overridable via CustomRole.permissions)
    "virtualKeys:view",
    "gatewayBudgets:view",
    "gatewayProviders:view",
    "routingPolicies:view",
    "gatewayGuardrails:view",
    "gatewayLogs:view",
    "auditLog:view",
    "gatewayUsage:view",
    "gatewayCacheRules:view",
  ],
};

/**
 * Define what permissions each organization role has
 */
const ORGANIZATION_ROLE_PERMISSIONS: Record<
  OrganizationUserRole,
  Permission[]
> = {
  [OrganizationUserRole.ADMIN]: [
    "organization:view",
    "organization:manage",
    "organization:delete",
    // Org admins get Langy at member level, and `langy:manage` additionally
    // gates the org-tier Langy administration. Connecting GitHub is gated by
    // `organization:manage` instead, because the connection is the
    // organization's rather than Langy's. Manage implies the rest via the
    // hierarchy rule.
    "langy:view",
    "langy:manage",
    // AI Governance — org-level permissions for the governance offering
    // (anomaly rules, ingestion sources, OCSF SIEM export, activity
    // monitor, top-level Govern section). Default-attached to ADMIN so
    // admins can bootstrap their first IngestionSource without a
    // chicken-and-egg gate. MEMBER + EXTERNAL get nothing by default;
    // custom roles via CustomRolePermissions JSON column are the
    // production-shape delegation surface (e.g. a "security_analyst"
    // custom role granting governance:view + activityMonitor:view +
    // anomalyRules:view).
    "governance:view",
    "governance:manage",
    "ingestionSources:view",
    "ingestionSources:create",
    "ingestionSources:update",
    "ingestionSources:delete",
    "ingestionSources:manage",
    "anomalyRules:view",
    "anomalyRules:create",
    "anomalyRules:update",
    "anomalyRules:delete",
    "anomalyRules:manage",
    "complianceExport:view",
    "activityMonitor:view",
    // AI Tools Portal — admin owns the catalog. View is implicit via
    // the org-wide grant below (admins also see the user-facing portal).
    "aiTools:view",
    "aiTools:manage",
    // AI Gateway — org-level VK capabilities. `virtualKeys:manage`
    // mirrors the TeamUserRole.ADMIN grant so org admins can author VKs
    // at ORGANIZATION scope (the team-role short-circuit at
    // rbac.ts:715/:1099 covers existing customers automatically; the
    // explicit string here documents the perm-listing UI + future custom
    // roles that don't inherit the short-circuit). `viewOtherPersonal`
    // gives org admins the off-boarding sweep capability. Spec-bound to
    // vk-scope-rbac.feature.
    "virtualKeys:manage",
    "virtualKeys:viewOtherPersonal",
    // Webhook platform: admin-only by default (endpoints carry signing
    // secrets and stream org-wide events out). Custom roles can delegate
    // webhookEndpoints:view for read-only delivery-log access.
    "webhookEndpoints:view",
    "webhookEndpoints:manage",
    // Spend reconciliation reads (spend events, end-user rollups) and the
    // operator write on that surface: replaying a window's spend
    // envelopes to one endpoint.
    "gatewaySpend:view",
    "gatewaySpend:manage",
    // The organization's cost screen (ADR-128). The matched half of the
    // `packages/authz` ORG_ADMIN bag — roles-parity.unit.test.ts fails if
    // only one of the two carries it, and a permission missing from either
    // is held by nobody.
    "governanceCost:view",
  ],
  // MEMBER + EXTERNAL get aiTools:view so the /me portal renders for
  // every org member. Catalog management stays admin-only.
  [OrganizationUserRole.MEMBER]: ["organization:view", "aiTools:view"],
  [OrganizationUserRole.EXTERNAL]: ["organization:view", "aiTools:view"],
};

/**
 * Default permission set for EXTERNAL (lite member) users.
 * Currently identical to VIEWER — lite members can view all resources but
 * cannot create, edit, or manage them. Maintained as a separate constant so
 * it can diverge from VIEWER independently if needed.
 *
 * Custom roles, when assigned to EXTERNAL users, override these defaults
 * (see resolveProjectPermission).
 *
 * @deprecated Use `builtinRolePermissions("lite-member")` from
 * `@langwatch/authz` instead — the engine owns the role bags now, and these
 * two are kept cell-for-cell identical by `roles-parity.unit.test.ts`. Reading
 * the bag from here is how a caller ends up disagreeing with the engine that
 * decides the request.
 */
export const EXTERNAL_MEMBER_PERMISSIONS: Permission[] = [
  "project:view",
  "analytics:view",
  "navigation:view",
  "traces:view",
  "annotations:view",
  "annotations:create",
  "annotations:update",
  "evaluations:view",
  "tasks:view",
  "datasets:view",
  "workflows:view",
  "experiments:view",
  "prompts:view",
  "scenarios:view",
  "secrets:view",
  "team:view",
];

// ============================================================================
// PERMISSION CHECKING
// ============================================================================

/**
 * Check if a permission list includes a requested permission, with hierarchy rules
 * manage permissions automatically include view, create, update, and delete permissions
 *
 * @deprecated Use `permissionSatisfiedBy` from `@langwatch/authz` instead —
 * the hierarchy rule belongs to the permission registry, and a second copy of
 * it can only drift from the one the engine applies.
 */
export function hasPermissionWithHierarchy(
  permissions: string[],
  requestedPermission: string,
): boolean {
  // Direct match
  if (permissions.includes(requestedPermission)) {
    return true;
  }

  // Hierarchy rule: manage permissions include view, create, update, delete,
  // and gateway-specific sub-actions (rotate, attach, detach).
  const actionSuffixes = [
    ":view",
    ":create",
    ":update",
    ":delete",
    ":rotate",
    ":attach",
    ":detach",
  ];
  for (const suffix of actionSuffixes) {
    if (requestedPermission.endsWith(suffix)) {
      const managePermission = requestedPermission.replace(suffix, ":manage");
      if (permissions.includes(managePermission)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if a team role has a specific permission
 *
 * @deprecated Use `builtinRoleGrants({ role: roleKeyForTeamRole(role),
 * permission })` from `@langwatch/authz` instead — the engine owns the role
 * bags, and this reads a second copy of them.
 */
export function teamRoleHasPermission(
  role: TeamUserRole,
  permission: Permission,
): boolean {
  return hasPermissionWithHierarchy(TEAM_ROLE_PERMISSIONS[role], permission);
}

/**
 * Check if an organization role has a specific permission
 *
 * @deprecated Use `builtinRoleGrants` from `@langwatch/authz` with the
 * `org-admin` or `org-member` role key instead. Note that an answer from here
 * is NOT an access decision: at organization scope the engine also applies the
 * membership gate and the org-member floor, so a caller wanting "may they do
 * this" wants `getApp().permissions`, not a role bag.
 */
export function organizationRoleHasPermission(
  role: OrganizationUserRole,
  permission: Permission,
): boolean {
  const rolePermissions = ORGANIZATION_ROLE_PERMISSIONS[role];
  return hasPermissionWithHierarchy(rolePermissions, permission);
}

/**
 * Get all permissions for a team role
 *
 * @deprecated Use `builtinRolePermissions(roleKeyForTeamRole(role))` from
 * `@langwatch/authz` instead.
 */
export function getTeamRolePermissions(role: TeamUserRole): Permission[] {
  return TEAM_ROLE_PERMISSIONS[role];
}

/**
 * Get all permissions for an organization role
 *
 * @deprecated Use `builtinRolePermissions` from `@langwatch/authz` with the
 * `org-admin`, `org-member` or `lite-member` role key instead.
 */
export function getOrganizationRolePermissions(
  role: OrganizationUserRole,
): Permission[] {
  return ORGANIZATION_ROLE_PERMISSIONS[role];
}

// ============================================================================
// HELPER FUNCTIONS FOR COMMON PERMISSION CHECKS
// ============================================================================

/**
 * Check if user can view a resource *
 * @deprecated Use `builtinRoleGrants` from `@langwatch/authz` instead, or
 * `getApp().permissions` when the question is really "may this caller do
 * this". A team role alone has never been the whole answer — bindings, custom
 * roles and the lite-member cap all sit on top of it — so these read as an
 * access check and are not one.
 */
export function canView(role: TeamUserRole, resource: Resource): boolean {
  return teamRoleHasPermission(role, `${resource}:view` as Permission);
}

/**
 * Check if user can manage a resource (full CRUD) *
 * @deprecated Use `builtinRoleGrants` from `@langwatch/authz` instead, or
 * `getApp().permissions` when the question is really "may this caller do
 * this". A team role alone has never been the whole answer — bindings, custom
 * roles and the lite-member cap all sit on top of it — so these read as an
 * access check and are not one.
 */
export function canManage(role: TeamUserRole, resource: Resource): boolean {
  return teamRoleHasPermission(role, `${resource}:manage` as Permission);
}

/**
 * Check if user can create a resource *
 * @deprecated Use `builtinRoleGrants` from `@langwatch/authz` instead, or
 * `getApp().permissions` when the question is really "may this caller do
 * this". A team role alone has never been the whole answer — bindings, custom
 * roles and the lite-member cap all sit on top of it — so these read as an
 * access check and are not one.
 */
export function canCreate(role: TeamUserRole, resource: Resource): boolean {
  return teamRoleHasPermission(role, `${resource}:create` as Permission);
}

/**
 * Check if user can update a resource *
 * @deprecated Use `builtinRoleGrants` from `@langwatch/authz` instead, or
 * `getApp().permissions` when the question is really "may this caller do
 * this". A team role alone has never been the whole answer — bindings, custom
 * roles and the lite-member cap all sit on top of it — so these read as an
 * access check and are not one.
 */
export function canUpdate(role: TeamUserRole, resource: Resource): boolean {
  return teamRoleHasPermission(role, `${resource}:update` as Permission);
}

/**
 * Check if user can delete a resource *
 * @deprecated Use `builtinRoleGrants` from `@langwatch/authz` instead, or
 * `getApp().permissions` when the question is really "may this caller do
 * this". A team role alone has never been the whole answer — bindings, custom
 * roles and the lite-member cap all sit on top of it — so these read as an
 * access check and are not one.
 */
export function canDelete(role: TeamUserRole, resource: Resource): boolean {
  return teamRoleHasPermission(role, `${resource}:delete` as Permission);
}

// ============================================================================
// PERMISSION RESULT TYPE
// ============================================================================

/**
 * Result of resolving a permission check, including the user's organization role.
 * Used by resolve* functions to provide richer context than a simple boolean.
 */
/**
 * @deprecated The answer shape of the fork-seam resolvers below, deprecated
 * with them. A caller that wants a decision wants `PermissionDecision` from
 * `~/server/app-layer/permissions/permission-decision.repository`.
 */
export type PermissionResult = {
  permitted: boolean;
  organizationRole: OrganizationUserRole | null;
  /**
   * Why the check failed, when there is something a caller can act on — the
   * boundary needs it to raise that error rather than a generic refusal.
   *
   * Set by the engine, and by the membership gate both heads share, which
   * names a disabled seat before either walk reads a binding. Absent from the
   * legacy binding walk itself, which never produced one and is being deleted
   * (ADR-110).
   */
  denialReason?: AuthzDenialReason;
};

// ============================================================================
// MIDDLEWARE & CONTEXT HELPERS
// ============================================================================

/**
 * @deprecated The parameter shape of the hand-`.use()`d middlewares below,
 * deprecated with them — see `checkProjectPermission`.
 */
export type PermissionMiddlewareParams<InputType> = {
  ctx: {
    prisma: PrismaClient;
    session: Session;
    permissionChecked: boolean;
    publiclyShared: boolean;
    organizationRole?: OrganizationUserRole | null;
    opsScope?: OpsScope;
  };
  input: InputType;
  next: () => any;
};

/**
 * @deprecated The shape of the hand-`.use()`d middlewares, deprecated with
 * them — see `checkProjectPermission`.
 */
export type PermissionMiddleware<InputType> = (
  params: PermissionMiddlewareParams<InputType>,
) => Promise<any>;

/**
 * A disabled seat outranks the role-shaped denials that follow it: the person
 * HAS a role, so the lite-member modal and the "ask an admin for this
 * permission" copy would both point them at something nobody can grant while
 * the seat is off.
 */
function membershipDisabledDenial(): TRPCError {
  const disabled = new MembershipDisabledError();
  return new TRPCError({
    code: "UNAUTHORIZED",
    message: disabled.message,
    cause: disabled,
  });
}

/**
 * Check if user has permission for a project.
 *
 * ADR-092 decision 25: procedures declare `.permission("…")` now — this
 * middleware survives only as a building block for the declared-custom
 * compositions that branch on their input (modelProviders' tenant anchor,
 * project creation). Do not `.use()` it on a new procedure.
 *
 * @deprecated Declare `.permission("…")` on the procedure instead
 * (ADR-092 decision 25). The declared surface is checked at compile time
 * against the input's scope ids and is what the router sweep audits; a
 * hand-`.use()`d middleware is invisible to both.
 */
export const checkProjectPermission =
  (permission: Permission) =>
  async ({
    ctx,
    input,
    next,
  }: PermissionMiddlewareParams<{ projectId: string }>) => {
    const { permitted, organizationRole, denialReason } =
      await resolveProjectPermission(ctx, input.projectId, permission);

    if (!permitted) {
      if (denialReason === "membership-disabled") {
        throw membershipDisabledDenial();
      }
      if (organizationRole === OrganizationUserRole.EXTERNAL) {
        throw new TRPCError({
          code: "UNAUTHORIZED",
          message: "This feature is not available for your account",
          cause: new LiteMemberRestrictedError(
            permission.split(":")[0] ?? "unknown",
          ),
        });
      }
      // `cause` carries the handled error, exactly as the EXTERNAL branch
      // above does. Without it this denial crossed the boundary as bare prose,
      // so the client had no code to key copy off and rendered the generic
      // "unknown" state for a refusal we can name — and one the customer can
      // act on by asking an admin for the permission named in `meta`.
      //
      // The wire code that results is FORBIDDEN, not the UNAUTHORIZED spelled
      // below: `handledErrorMiddleware` re-derives it from the handled cause's
      // `httpStatus` (403). That is the right answer — the caller IS
      // authenticated, they just lack the permission — and it now matches what
      // the REST surface has always returned for the same refusal.
      throw new TRPCError({
        code: "UNAUTHORIZED",
        message: "You do not have permission to access this project resource",
        cause: new ProjectPermissionDeniedError(permission),
      });
    }

    ctx.organizationRole = organizationRole;
    ctx.permissionChecked = true;
    return next();
  };

/**
 * Check if user has permission for a team
 *
 * @deprecated Declare `.permission("…")` on the procedure instead
 * (ADR-092 decision 25). The declared surface is checked at compile time
 * against the input's scope ids and is what the router sweep audits; a
 * hand-`.use()`d middleware is invisible to both.
 */
export const checkTeamPermission =
  (permission: Permission) =>
  async ({
    ctx,
    input,
    next,
  }: PermissionMiddlewareParams<{ teamId: string }>) => {
    const { permitted, organizationRole, denialReason } =
      await resolveTeamPermission(ctx, input.teamId, permission);

    if (!permitted) {
      if (denialReason === "membership-disabled") {
        throw membershipDisabledDenial();
      }
      if (organizationRole === OrganizationUserRole.EXTERNAL) {
        throw new TRPCError({
          code: "UNAUTHORIZED",
          message: "This feature is not available for your account",
          cause: new LiteMemberRestrictedError(
            permission.split(":")[0] ?? "unknown",
          ),
        });
      }
      throw new TRPCError({
        code: "UNAUTHORIZED",
        message: "You do not have permission to access this team resource",
      });
    }

    ctx.organizationRole = organizationRole;
    ctx.permissionChecked = true;
    return next();
  };

/**
 * Check if user has permission for an organization
 *
 * @deprecated Declare `.permission("…")` on the procedure instead
 * (ADR-092 decision 25). The declared surface is checked at compile time
 * against the input's scope ids and is what the router sweep audits; a
 * hand-`.use()`d middleware is invisible to both.
 */
export const checkOrganizationPermission =
  (permission: Permission) =>
  async ({
    ctx,
    input,
    next,
  }: PermissionMiddlewareParams<{ organizationId: string }>) => {
    if (
      !(await hasOrganizationPermission(ctx, input.organizationId, permission))
    ) {
      throw new TRPCError({
        code: "UNAUTHORIZED",
        message:
          "You do not have permission to access this organization resource",
      });
    }

    ctx.permissionChecked = true;
    return next();
  };

// ============================================================================
// BACKEND PERMISSION CHECKS
// ============================================================================

// ============================================================================
// ROLE BINDING RESOLUTION
// ============================================================================

/**
 * Checks whether any of the user's RoleBindings at the given scopes grants the
 * requested permission. All matching bindings are evaluated and their permission
 * sets are unioned — a user is permitted if ANY binding grants the permission.
 *
 * Falls back to the legacy TeamUser table when no RoleBindings exist.
 */
async function checkPermissionFromBindings({
  prisma,
  userId,
  organizationId,
  scopes,
  organizationRole,
  permission,
}: {
  prisma: PrismaClient;
  userId: string;
  organizationId: string;
  scopes: Array<{ scopeType: RoleBindingScopeType; scopeId: string }>;
  organizationRole: OrganizationUserRole | null;
  permission: Permission;
}): Promise<boolean> {
  const scopeIds = scopes.map((s) => s.scopeId);

  // Fetch groups the user belongs to in this org
  const groupMemberships = await prisma.groupMembership.findMany({
    where: { userId, group: { organizationId } },
    select: { groupId: true },
  });
  const groupIds = groupMemberships.map((m) => m.groupId);

  // Fetch all matching RoleBindings for this user (direct + via groups) across all scopes.
  // The direct-binding branch is gated on current organization membership so a
  // stale cross-org binding (one naming this user at a scope in an org they no
  // longer/never belonged to) is never selected. Membership — not the binding
  // row — is the tenancy boundary.
  const bindings = await prisma.roleBinding.findMany({
    where: {
      organizationId,
      scopeId: { in: scopeIds },
      OR: [
        {
          userId,
          user: {
            orgMemberships: { some: { organizationId, disabledAt: null } },
          },
        },
        ...(groupIds.length > 0 ? [{ groupId: { in: groupIds } }] : []),
      ],
    },
    select: { role: true, customRoleId: true, scopeType: true },
  });

  if (bindings.length === 0) {
    // Fall back to legacy TeamUser for users not yet migrated to RoleBindings.
    // The fallback stays live for EVERY organization — backfilled, cut over,
    // or untouched — until contract deletes the rows: stage B's finalization
    // proves the promoted bindings answer identically at the scopes they
    // replace, and the engine keeps inferring from the same rows on both
    // heads (the dormant-fact principle), so switching this off early made
    // the readers disagree instead of making the rows dead.
    const teamScope = scopes.find(
      (s) => s.scopeType === RoleBindingScopeType.TEAM,
    );
    if (!teamScope) return false;

    const teamUser = await prisma.teamUser.findFirst({
      // Gate the legacy fallback on org membership too: a stale cross-org
      // TeamUser row must not confer access any more than a stale RoleBinding,
      // and neither does a seat an admin disabled.
      where: {
        userId,
        teamId: teamScope.scopeId,
        team: {
          organization: { members: { some: { userId, disabledAt: null } } },
        },
      },
      select: { role: true, assignedRoleId: true },
    });

    if (!teamUser) return false;
    // Legacy team membership is a TEAM-scoped grant, so it can't confer an
    // org-exclusive permission even through a custom role (ADR-021).
    if (!bindingScopeCanGrant(RoleBindingScopeType.TEAM, permission)) {
      return false;
    }
    return resolveBindingPermission({
      binding: {
        role: teamUser.role,
        customRoleId: teamUser.assignedRoleId ?? null,
      },
      organizationId,
      organizationRole,
      permission,
      prisma,
    });
  }

  // Union permissions across ALL matching bindings — permitted if any grants it
  for (const binding of bindings) {
    // A team/project binding can never grant an org-exclusive permission,
    // even via a custom role that lists it (ADR-021).
    if (!bindingScopeCanGrant(binding.scopeType, permission)) continue;

    // Org-scoped bindings: ADMIN grants everything; MEMBER grants org-level permissions only.
    // ORG-scoped MEMBER bindings do NOT imply any team- or project-level access — team/project
    // access requires a TEAM- or PROJECT-scoped binding. Only org:* permissions are checked here.
    if (
      binding.scopeType === RoleBindingScopeType.ORGANIZATION &&
      binding.role !== TeamUserRole.CUSTOM
    ) {
      // Defense-in-depth: EXTERNAL (Lite Member) users must never be promoted
      // by this fast path even if an ORG-scoped MEMBER binding exists — the
      // OrganizationUser role is authoritative for EXTERNAL restrictions.
      if (organizationRole === OrganizationUserRole.EXTERNAL) continue;
      if (binding.role === TeamUserRole.ADMIN) return true;
      if (
        organizationRoleHasPermission(OrganizationUserRole.MEMBER, permission)
      )
        return true;
      continue;
    }

    const permitted = await resolveBindingPermission({
      binding,
      organizationId,
      organizationRole,
      permission,
      prisma,
    });
    if (permitted) return true;
  }

  return false;
}

/**
 * Checks whether a single binding grants the requested permission,
 * respecting EXTERNAL user restrictions and custom role permission lists.
 *
 * See also checkRoleBindingPermission() in role-binding-resolver.ts which
 * implements parallel CUSTOM-role logic for API key resolution and must
 * stay in sync with the non-empty/empty fallthrough semantics here.
 */
async function resolveBindingPermission({
  binding,
  organizationId,
  organizationRole,
  permission,
  prisma,
}: {
  binding: { role: TeamUserRole; customRoleId: string | null };
  organizationId: string;
  organizationRole: OrganizationUserRole | null;
  permission: Permission;
  prisma: PrismaClient;
}): Promise<boolean> {
  if (binding.customRoleId) {
    // Defense in depth, same two axes as the API-key resolver: the lookup is
    // scoped to the organization being checked, so a poisoned binding pointing
    // at another organization's role grants nothing of that role; and this
    // path resolves session users and groups only, so an API key's private
    // permission role (kind system_api_key) never backs it. Either mismatch
    // resolves exactly like a missing role: the built-in fallthrough below.
    const customRole = await prisma.customRole.findFirst({
      where: {
        id: binding.customRoleId,
        organizationId,
        kind: { not: CUSTOM_ROLE_KIND.SYSTEM_API_KEY },
      },
    });
    if (customRole) {
      const perms = Array.isArray(customRole.permissions)
        ? (customRole.permissions as string[])
        : [];
      if (perms.length > 0) {
        return hasPermissionWithHierarchy(perms, permission);
      }
    }
  }

  if (organizationRole === OrganizationUserRole.EXTERNAL) {
    return hasPermissionWithHierarchy(EXTERNAL_MEMBER_PERMISSIONS, permission);
  }

  return teamRoleHasPermission(binding.role, permission);
}

/**
 * The membership row as stored: the role, and whether an admin disabled the
 * seat. Both are facts. The policy that a disabled membership is not a
 * current one lives in `currentRoleOf`, the same split the AuthZ read port
 * makes, so a refusal can still say WHICH gate closed.
 */
type CurrentMembership = {
  role: OrganizationUserRole;
  disabled: boolean;
};

async function getCurrentMembership({
  prisma,
  userId,
  organizationId,
}: {
  prisma: PrismaClient;
  userId: string;
  organizationId: string;
}): Promise<CurrentMembership | null> {
  const member = await prisma.organizationUser?.findFirst({
    where: { userId, organizationId },
    select: { role: true, disabledAt: true },
  });
  if (!member) return null;
  // `!== null`, not `!= null`: a row arriving without the column reads as
  // DISABLED, the same loud-lockout choice the read repository makes.
  return { role: member.role, disabled: member.disabledAt !== null };
}

/**
 * A membership an admin disabled to reclaim its seat is not a current one: it
 * keeps its role and its history and confers no access until it is re-enabled
 * (seat-reconciliation.feature). Matches the policy the AuthZ collector
 * applies to the same row.
 */
function currentRoleOf(
  membership: CurrentMembership | null,
): OrganizationUserRole | null {
  return membership && !membership.disabled ? membership.role : null;
}

/**
 * Why a membership read refused, when there is something to say. A disabled
 * seat is named so the boundary can tell the person their access was turned
 * off rather than that they are not a member. An absent row says nothing
 * here: the generic denial is already the right answer for it.
 */
function membershipDenialReason(
  membership: CurrentMembership | null,
): AuthzDenialReason | undefined {
  return membership?.disabled ? "membership-disabled" : undefined;
}

/**
 * The single source of truth for "is this user a CURRENT member of this org,
 * and with what role". `null` means no active `OrganizationUser` row — the
 * caller must fail closed rather than fall through to bindings, because a
 * stale cross-org RoleBinding can still name a non-member at a project/team
 * scope.
 *
 * Every scoped permission path derives membership through here so a future
 * tenancy fix lands once instead of in each resolver.
 */
async function getCurrentOrganizationRole(args: {
  prisma: PrismaClient;
  userId: string;
  organizationId: string;
}): Promise<OrganizationUserRole | null> {
  return currentRoleOf(await getCurrentMembership(args));
}

/** A denial reached before any binding was read. */
function refused(denialReason?: AuthzDenialReason): PermissionResult {
  return {
    permitted: false,
    organizationRole: null,
    ...(denialReason ? { denialReason } : {}),
  };
}

type ProjectPermissionContext = {
  userId: string;
  teamId: string;
  organizationId: string;
  organizationRole: OrganizationUserRole;
};

/**
 * The context resolved to a denial before any binding was read, carrying
 * the reason when there is one a caller can act on.
 */
type ProjectPermissionRefusal = {
  refused: true;
  denialReason?: AuthzDenialReason;
};

/**
 * Where a project sits and where the caller stands in it: everything a
 * permission check needs before it looks at a single binding. A refusal when
 * the answer is already a denial, either because the project has no team or
 * organization or because the caller is not an active member of that
 * organization.
 *
 * Resolved on its own so a gate that tests several permissions pays for it
 * once: only the binding check below differs from permission to permission.
 */
async function resolveProjectPermissionContext(
  ctx: { prisma: PrismaClient; session: Session | null },
  projectId: string,
): Promise<ProjectPermissionContext | ProjectPermissionRefusal> {
  const userId = ctx.session?.user?.id;
  if (!userId) return { refused: true };

  const projectTeam = await ctx.prisma.project.findUnique?.({
    where: { id: projectId },
    select: { team: { select: { id: true, organizationId: true } } },
  });

  const teamId = projectTeam?.team.id;
  const organizationId = projectTeam?.team.organizationId;

  if (!teamId || !organizationId) return { refused: true };

  const membership = await getCurrentMembership({
    prisma: ctx.prisma,
    userId,
    organizationId,
  });
  const organizationRole = currentRoleOf(membership);

  // Fail closed on current organization membership. A user who is not an
  // active OrganizationUser of the owning org is denied outright, even if a
  // stale RoleBinding (created through a since-closed cross-org path) still
  // names them at this project/team scope. The membership check — not the
  // binding row — is the authoritative tenancy boundary. See the same gate in
  // resolveTeamPermission and batchScopePermissions, and the direct-binding
  // predicate below. A disabled seat is refused here too, before either head
  // runs, and named so the boundary can say so.
  if (organizationRole === null) {
    return { refused: true, denialReason: membershipDenialReason(membership) };
  }

  return { userId, teamId, organizationId, organizationRole };
}

function projectPermissionScopes({
  projectId,
  teamId,
  organizationId,
}: {
  projectId: string;
  teamId: string;
  organizationId: string;
}) {
  return [
    { scopeType: RoleBindingScopeType.PROJECT, scopeId: projectId },
    { scopeType: RoleBindingScopeType.TEAM, scopeId: teamId },
    { scopeType: RoleBindingScopeType.ORGANIZATION, scopeId: organizationId },
  ];
}

/**
 * Resolve a project permission check, returning the permission decision
 * along with the user's organization role.
 */
/**
 * @deprecated Not for new callers. This is the ADR-110 fork seam: it asks
 * the engine for a migrated organization and runs the legacy walk for one
 * that is not, and it goes away with the walk. New code decides through
 * `getApp().permissions` (or `probe*Permission` /
 * `require*Permission` in `~/server/app-layer/permissions/imperative`),
 * which reaches the same engine without depending on the fork surviving.
 */
export async function resolveProjectPermission(
  ctx: { prisma: PrismaClient; session: Session | null },
  projectId: string,
  permission: Permission,
): Promise<PermissionResult> {
  if (!ctx.session?.user) {
    return { permitted: false, organizationRole: null };
  }

  // The demo project grants its view permissions to everyone, before
  // anything is read. The engine carries the same rule.
  if (isDemoProject(projectId, permission)) {
    return { permitted: true, organizationRole: null };
  }

  const context = await resolveProjectPermissionContext(ctx, projectId);
  if ("refused" in context) return refused(context.denialReason);

  /** The legacy binding walk, run only while this organization is still
   *  waiting for its migration. */
  const legacyPermitted = () =>
    checkPermissionFromBindings({
      prisma: ctx.prisma,
      userId: context.userId,
      organizationId: context.organizationId,
      scopes: projectPermissionScopes({
        projectId,
        teamId: context.teamId,
        organizationId: context.organizationId,
      }),
      organizationRole: context.organizationRole,
      permission,
    });

  // ADR-110: finishing the migration IS the switch. An organization that
  // finished is decided by the engine; one that has not is decided by the
  // legacy walk.
  if (
    await organizationOnAuthzEngine({
      prisma: ctx.prisma,
      organizationId: context.organizationId,
    })
  ) {
    const decision = await authzChecksFor(ctx.prisma).checkByIds({
      principal: { type: "user", id: context.userId },
      permission,
      projectId,
    });
    return {
      permitted: decision.allowed,
      // The engine's snapshot read the same OrganizationUser row the context
      // above did, so these agree; the context's is the fallback for the
      // unresolved-scope answer, which carries no snapshot at all.
      organizationRole: decision.organizationRole ?? context.organizationRole,
      denialReason: decision.denialReason,
    };
  }

  return {
    permitted: await legacyPermitted(),
    organizationRole: context.organizationRole,
  };
}

/**
 * Resolve a check that any one of `permissions` is enough, in the order given.
 * The project, the team, the organization and the caller's role in it are the
 * same for every permission, so they are read once and only the binding check
 * repeats.
 */
/**
 * @deprecated Not for new callers. This is the ADR-110 fork seam: it asks
 * the engine for a migrated organization and runs the legacy walk for one
 * that is not, and it goes away with the walk. New code decides through
 * `getApp().permissions` (or `probe*Permission` /
 * `require*Permission` in `~/server/app-layer/permissions/imperative`),
 * which reaches the same engine without depending on the fork surviving.
 */
export async function resolveProjectPermissionAny(
  ctx: { prisma: PrismaClient; session: Session | null },
  projectId: string,
  permissions: readonly Permission[],
): Promise<PermissionResult> {
  if (!ctx.session?.user) {
    return { permitted: false, organizationRole: null };
  }

  // The demo project grants its view permissions to everyone, so one of them
  // being enough settles the question before anything is read.
  if (permissions.some((permission) => isDemoProject(projectId, permission))) {
    return { permitted: true, organizationRole: null };
  }

  const context = await resolveProjectPermissionContext(ctx, projectId);
  if ("refused" in context) return refused(context.denialReason);

  const scopes = projectPermissionScopes({
    projectId,
    teamId: context.teamId,
    organizationId: context.organizationId,
  });

  // ADR-110: the engine answers every candidate off one collected snapshot,
  // in the same order, stopping at the same first allow the legacy loop
  // below would have stopped at.
  if (
    await organizationOnAuthzEngine({
      prisma: ctx.prisma,
      organizationId: context.organizationId,
    })
  ) {
    const decision = await authzChecksFor(ctx.prisma).canAnyByIds({
      principal: { type: "user", id: context.userId },
      permissions,
      projectId,
    });
    return {
      permitted: decision.allowed,
      organizationRole: decision.organizationRole ?? context.organizationRole,
      denialReason: decision.denialReason,
    };
  }

  for (const permission of permissions) {
    const permitted = await checkPermissionFromBindings({
      prisma: ctx.prisma,
      userId: context.userId,
      organizationId: context.organizationId,
      scopes,
      organizationRole: context.organizationRole,
      permission,
    });
    if (permitted) {
      return { permitted: true, organizationRole: context.organizationRole };
    }
  }

  return { permitted: false, organizationRole: context.organizationRole };
}

/**
 * Check if user has a specific permission for a project.
 *
 * @deprecated Production callers use the App-routed helpers in
 * `~/server/app-layer/permissions/imperative` (`probe*Permission` for a
 * boolean, `require*Permission` for the throwing form that returns the
 * Authorized witness) — this stays only as the legacy walk's own test
 * surface until the contract PR deletes the walk. The names no longer
 * collide, so a mock can no longer bind the wrong module silently.
 */
export async function hasProjectPermission(
  ctx: { prisma: PrismaClient; session: Session | null },
  projectId: string,
  permission: Permission,
): Promise<boolean> {
  const result = await resolveProjectPermission(ctx, projectId, permission);
  return result.permitted;
}

/**
 * Resolve a team permission check, returning the permission decision
 * along with the user's organization role.
 */
/**
 * @deprecated Not for new callers. This is the ADR-110 fork seam: it asks
 * the engine for a migrated organization and runs the legacy walk for one
 * that is not, and it goes away with the walk. New code decides through
 * `getApp().permissions` (or `probe*Permission` /
 * `require*Permission` in `~/server/app-layer/permissions/imperative`),
 * which reaches the same engine without depending on the fork surviving.
 */
export async function resolveTeamPermission(
  ctx: { prisma: PrismaClient; session: Session | null },
  teamId: string,
  permission: Permission,
): Promise<PermissionResult> {
  if (!ctx.session?.user) {
    return { permitted: false, organizationRole: null };
  }

  const team = await ctx.prisma.team.findUnique?.({
    where: { id: teamId },
    include: {},
  });

  if (!team?.organizationId) {
    return { permitted: false, organizationRole: null };
  }

  const membership = await getCurrentMembership({
    prisma: ctx.prisma,
    userId: ctx.session.user.id,
    organizationId: team.organizationId,
  });
  const organizationRole = currentRoleOf(membership);

  // Fail closed on current organization membership — a non-member is denied
  // even if a stale cross-org binding names them at this team scope, and a
  // disabled seat is refused by name. See the matching gate in
  // resolveProjectPermission.
  if (organizationRole === null) {
    return refused(membershipDenialReason(membership));
  }

  const userId = ctx.session.user.id;

  /** The legacy binding walk, as ONE implementation both paths run. */
  const legacyPermitted = () =>
    checkPermissionFromBindings({
      prisma: ctx.prisma,
      userId,
      organizationId: team.organizationId,
      scopes: [
        { scopeType: RoleBindingScopeType.TEAM, scopeId: teamId },
        {
          scopeType: RoleBindingScopeType.ORGANIZATION,
          scopeId: team.organizationId,
        },
      ],
      organizationRole,
      permission,
    });

  // The organization is the one the team read above already resolved.
  if (
    await organizationOnAuthzEngine({
      prisma: ctx.prisma,
      organizationId: team.organizationId,
    })
  ) {
    const decision = await authzChecksFor(ctx.prisma).checkByIds({
      principal: { type: "user", id: userId },
      permission,
      teamId,
    });
    return {
      permitted: decision.allowed,
      organizationRole: decision.organizationRole ?? organizationRole,
      denialReason: decision.denialReason,
    };
  }

  return { permitted: await legacyPermitted(), organizationRole };
}

/**
 * Check if user has a specific permission for a team.
 *
 * @deprecated Production callers use the App-routed helpers in
 * `~/server/app-layer/permissions/imperative` (`probe*Permission` for a
 * boolean, `require*Permission` for the throwing form that returns the
 * Authorized witness) — this stays only as the legacy walk's own test
 * surface until the contract PR deletes the walk. The names no longer
 * collide, so a mock can no longer bind the wrong module silently.
 */
export async function hasTeamPermission(
  ctx: { prisma: PrismaClient; session: Session | null },
  teamId: string,
  permission: Permission,
): Promise<boolean> {
  const result = await resolveTeamPermission(ctx, teamId, permission);
  return result.permitted;
}

/**
 * Check if user has a specific permission for an organization
 */
/**
 * @deprecated Not for new callers. This is the ADR-110 fork seam: it asks
 * the engine for a migrated organization and runs the legacy walk for one
 * that is not, and it goes away with the walk. New code decides through
 * `getApp().permissions` (or `probe*Permission` /
 * `require*Permission` in `~/server/app-layer/permissions/imperative`),
 * which reaches the same engine without depending on the fork surviving.
 */
export async function hasOrganizationPermission(
  ctx: { prisma: PrismaClient; session: Session },
  organizationId: string,
  permission: Permission,
): Promise<boolean> {
  const userId = ctx.session?.user?.id;

  if (
    userId &&
    (await organizationOnAuthzEngine({ prisma: ctx.prisma, organizationId }))
  ) {
    const decision = await authzChecksFor(ctx.prisma).checkByIds({
      principal: { type: "user", id: userId },
      permission,
      organizationId,
    });
    return decision.allowed;
  }

  return hasOrganizationPermissionLegacy(ctx, organizationId, permission);
}

/**
 * Why an organization-scope check refused, asked only after it did: one
 * indexed read on the refusal path rather than a second walk, so the
 * permitted path pays nothing. Undefined when there is nothing more useful
 * to say than the generic denial.
 */
export async function organizationDenialReason({
  ctx,
  organizationId,
}: {
  ctx: { prisma: PrismaClient; session: Session };
  organizationId: string;
}): Promise<AuthzDenialReason | undefined> {
  const userId = ctx.session?.user?.id;
  if (!userId) return undefined;
  return membershipDenialReason(
    await getCurrentMembership({ prisma: ctx.prisma, userId, organizationId }),
  );
}

async function hasOrganizationPermissionLegacy(
  ctx: { prisma: PrismaClient; session: Session },
  organizationId: string,
  permission: Permission,
): Promise<boolean> {
  if (!ctx.session?.user) {
    return false;
  }

  const userId = ctx.session.user.id;

  const orgMember = await ctx.prisma.organizationUser?.findFirst({
    // A seat-disabled membership confers nothing, exactly as an absent one
    // does — see getCurrentOrganizationRole above.
    where: { userId, organizationId, disabledAt: null },
    select: { role: true },
  });

  if (!orgMember) return false;

  // EXTERNAL (Lite Member) is a billing classification, not an access-control
  // boundary, so it must NOT cap organization-permission resolution. Removing
  // the old `organization:view`-only short-circuit lets a lite member reach the
  // MEMBER base bag below (`organization:view` + `aiTools:view`), which is what
  // the /me AI-tools portal needs to render. The binding-level guards in
  // `checkPermissionFromBindings` are unchanged: they still skip non-CUSTOM
  // ORGANIZATION-scoped bindings for EXTERNAL and cap non-CUSTOM team bindings
  // at EXTERNAL_MEMBER_PERMISSIONS, so a lite member does not escalate through
  // the default role bag. An explicit CUSTOM role binding is still honored for
  // EXTERNAL — that is the intended admin delegation surface (see the
  // EXTERNAL_MEMBER_PERMISSIONS docs), and it is unchanged here. So beyond the
  // MEMBER base bag this change adds, a lite member only ever gains what a
  // custom role was deliberately granted. (Fully retiring EXTERNAL as a
  // permission gate is the follow-up for when it becomes a computed property.)
  //
  // Regression: the short-circuit fired before the floor below and hid
  // `aiTools:view`, so a lite member's /me portal `aiTools.list` threw
  // UNAUTHORIZED and rendered the empty "your admin hasn't added any tools"
  // state even when an org-wide tool was published (customer report).

  // Universal personal-context floor: every org member, regardless of
  // role, gets MEMBER's base bag (`organization:view` + `aiTools:view`)
  // so /me works. Without this floor, a bare org-member with no team
  // membership AND no custom RoleBinding fell through every check
  // below and was rejected from every personal-context tRPC procedure
  // (user.personalContext / personalUsage / personalBudget /
  // homePagePickerState / governance.resolveHome / limits.getUsage /
  // aiTools.list — all gated on `organization:view`). Caught when
  // MEMBER `rogerio@…` was added to an org for the Claude Code OTLP
  // dogfood and his /me page permission-denied silently — the page
  // rendered as if no data existed instead of "no access".
  //
  // Critical: floor is MEMBER's bag *only*, NOT the role's full bag.
  // ADMIN-only org perms (`organization:manage` / `governance:manage`
  // / `ingestionSources:create` / etc.) still require an explicit
  // ORGANIZATION-scoped RoleBinding. A bare OrgUser.role=ADMIN with
  // no RoleBinding doesn't escalate — the existing legacy fallback
  // semantics expect RoleBindings (primary path) or TeamUser ADMIN
  // (limited team-resource fallback) to be the source of admin
  // power, not the OrgUser.role field by itself.
  if (organizationRoleHasPermission(OrganizationUserRole.MEMBER, permission)) {
    return true;
  }

  // Primary path: resolve via ORGANIZATION-scoped RoleBindings.
  const permittedByBindings = await checkPermissionFromBindings({
    prisma: ctx.prisma,
    userId,
    organizationId,
    scopes: [
      { scopeType: RoleBindingScopeType.ORGANIZATION, scopeId: organizationId },
    ],
    organizationRole: orgMember.role,
    permission,
  });
  if (permittedByBindings) return true;

  // Legacy fallback: users migrated before RoleBindings existed keep their
  // TeamUser row (with ADMIN/MEMBER/VIEWER role) but may have zero
  // RoleBindings. For org-scoped permission checks we union across every
  // TeamUser the user has in the organization — this matches the intent
  // that org ADMINs / team ADMINs have broad access to org-scoped gateway
  // resources (audit, org-level budgets, cache rules) without requiring a
  // RoleBinding backfill first.
  //
  // Personal teams are excluded: every user is ADMIN of their own
  // single-member personal workspace team, so unioning it here would let
  // any member escalate to the full org ADMIN template (including
  // virtualKeys:viewOtherPersonal / organization:manage) just by owning a
  // personal workspace. A personal team's legitimate ADMIN power is
  // team-scoped and flows through its TEAM-scoped RoleBinding, never this
  // org-wide union.
  // The team-membership union below is a TEAM-scoped grant applied to an
  // org-level check; org-exclusive permissions (organization:* / governance
  // family) are never conferred through it — only an ORGANIZATION-scoped
  // binding can (ADR-021). Gateway/audit resources stay grantable here.
  if (!bindingScopeCanGrant(RoleBindingScopeType.TEAM, permission)) {
    return false;
  }
  const teamMemberships = await ctx.prisma.teamUser.findMany({
    where: { userId, team: { organizationId, isPersonal: false } },
    select: { role: true, assignedRoleId: true },
  });
  for (const tu of teamMemberships) {
    const permitted = await resolveBindingPermission({
      binding: { role: tu.role, customRoleId: tu.assignedRoleId ?? null },
      organizationId,
      organizationRole: orgMember.role,
      permission,
      prisma: ctx.prisma,
    });
    if (permitted) return true;
  }
  return false;
}

/**
 * Batched team + project permission check used by surfaces that need to
 * test the SAME permission across many scopes inside one organization
 * (e.g. the model-defaults settings page enumerating every team +
 * project the caller can read/write). One scoped permission check costs
 * ~3-5 queries (team/project lookup, organizationUser, groupMembership,
 * roleBinding, optional customRole). N team + M project checks ran in a
 * Promise.all fan-out, that's hundreds of queries per page load on large
 * orgs.
 *
 * This helper does the four lookups ONCE — groupMembership, roleBinding
 * (with scopeId IN the union of all team/project/org ids), customRole
 * (for any binding referencing one), and legacy teamUser — then
 * resolves each id in-memory against the same rules
 * `checkPermissionFromBindings` applies.
 *
 * Project resolution still needs to know the project's team so a
 * team-scoped binding inherits to its projects. Callers pass the
 * project→teamId map alongside the project ids.
 */
/**
 * The permission-INDEPENDENT half of a scoped permission check, loaded once.
 *
 * Every lookup a scoped check makes — the caller's org membership, their group
 * memberships, the role bindings on the scopes in play, the custom roles those
 * bindings reference, and the legacy TeamUser fallback — is the same regardless
 * of WHICH permission is being asked about. Only `bindingGrants` consults the
 * permission, and it is pure.
 *
 * Separating the two is what lets a caller ask about N permissions for the price
 * of one round of queries instead of N rounds. That is not merely a speed-up: a
 * caller that fanned N checks out concurrently wanted N connections from the
 * Prisma pool AT ONCE, and starved anything sharing it — including an interactive
 * transaction with a 5s budget, which then aborted and failed the request
 * outright. Fewer queries is the fix; making the same queries faster is not.
 */
type ScopeResolution = {
  organizationRole: OrganizationUserRole | null;
  bindingsByScope: Map<string, ResolvedBinding[]>;
  customRoleById: Map<string, { id: string; permissions: unknown }>;
  /** No RoleBindings at all for this user ⇒ fall back to legacy TeamUser roles. */
  needsLegacyFallback: boolean;
  legacyByTeam: Map<
    string,
    { role: TeamUserRole; assignedRoleId: string | null }
  >;
};

type ResolvedBinding = {
  role: TeamUserRole;
  customRoleId: string | null;
  scopeType: RoleBindingScopeType;
  scopeId: string;
};

const scopeKey = (scopeType: RoleBindingScopeType, scopeId: string) =>
  `${scopeType}::${scopeId}`;

/**
 * Loads everything a scoped permission decision needs, in ~4 queries, for ANY
 * number of permissions and scopes. Returns null when the caller is not a member
 * of the organization at all — the "no" that short-circuits every question.
 */
async function loadScopeResolution(
  ctx: { prisma: PrismaClient; session: Session | null },
  args: { organizationId: string; scopeIds: string[] },
): Promise<ScopeResolution | null> {
  const userId = ctx.session?.user?.id;
  if (!userId) return null;

  const organizationRole = await getCurrentOrganizationRole({
    prisma: ctx.prisma,
    userId,
    organizationId: args.organizationId,
  });
  // Fail closed on current membership — a non-member gets nothing, even if a
  // stale cross-org binding names them at one of these scopes.
  if (organizationRole === null) return null;

  const groupMemberships = await ctx.prisma.groupMembership.findMany({
    where: { userId, group: { organizationId: args.organizationId } },
    select: { groupId: true },
  });
  const groupIds = groupMemberships.map((m) => m.groupId);

  const scopeIds = [args.organizationId, ...args.scopeIds];
  const bindings: ResolvedBinding[] =
    scopeIds.length > 0
      ? await ctx.prisma.roleBinding.findMany({
          where: {
            organizationId: args.organizationId,
            scopeId: { in: scopeIds },
            // Non-membership already short-circuits above, but gate the direct
            // branch on membership too so this query is safe on its own if the
            // early return is ever refactored away.
            OR: [
              {
                userId,
                user: {
                  orgMemberships: {
                    some: {
                      organizationId: args.organizationId,
                      disabledAt: null,
                    },
                  },
                },
              },
              ...(groupIds.length > 0 ? [{ groupId: { in: groupIds } }] : []),
            ],
          },
          select: {
            role: true,
            customRoleId: true,
            scopeType: true,
            scopeId: true,
          },
        })
      : [];

  const customRoleIds = Array.from(
    new Set(
      bindings.map((b) => b.customRoleId).filter((id): id is string => !!id),
    ),
  );
  const customRoles =
    customRoleIds.length > 0
      ? await ctx.prisma.customRole.findMany({
          // Same organization- and kind-scoping as resolveBindingPermission:
          // a poisoned binding referencing another organization's role, or an
          // API key's system_api_key role, must resolve like a missing role.
          // This loader only ever backs session-user and group resolution.
          where: {
            id: { in: customRoleIds },
            organizationId: args.organizationId,
            kind: { not: CUSTOM_ROLE_KIND.SYSTEM_API_KEY },
          },
          select: { id: true, permissions: true },
        })
      : [];

  const bindingsByScope = new Map<string, ResolvedBinding[]>();
  for (const b of bindings) {
    const key = scopeKey(b.scopeType, b.scopeId);
    const list = bindingsByScope.get(key) ?? [];
    list.push(b);
    bindingsByScope.set(key, list);
  }

  // Legacy fallback: a user with NO RoleBindings anywhere in the org falls back
  // to their TeamUser role. Mirrored here so the batch paths keep exact parity
  // with the per-call helpers. The fallback stays live until contract deletes
  // the rows — see the same note in checkPermissionFromBindings.
  const needsLegacyFallback = bindings.length === 0;
  const legacyTeamUser = needsLegacyFallback
    ? await ctx.prisma.teamUser.findMany({
        where: { userId, team: { organizationId: args.organizationId } },
        select: { teamId: true, role: true, assignedRoleId: true },
      })
    : [];

  return {
    organizationRole,
    bindingsByScope,
    customRoleById: new Map(customRoles.map((c) => [c.id, c])),
    needsLegacyFallback,
    legacyByTeam: new Map(
      legacyTeamUser.map((t) => [
        t.teamId,
        { role: t.role, assignedRoleId: t.assignedRoleId },
      ]),
    ),
  };
}

/**
 * Does this ONE binding grant this ONE permission? Pure — no I/O.
 *
 * This is the only part of a scoped check that depends on the permission, which
 * is why the loader above can be shared across all of them.
 */
function bindingGrants(
  resolution: ScopeResolution,
  binding: {
    role: TeamUserRole;
    customRoleId: string | null;
    scopeType: RoleBindingScopeType;
  },
  permission: Permission,
): boolean {
  // A team/project binding can never grant an org-exclusive permission, even via
  // a custom role that lists it (ADR-021).
  if (!bindingScopeCanGrant(binding.scopeType, permission)) return false;

  if (binding.customRoleId) {
    const custom = resolution.customRoleById.get(binding.customRoleId);
    if (custom) {
      const perms = Array.isArray(custom.permissions)
        ? (custom.permissions as string[])
        : [];
      if (perms.length > 0) {
        return hasPermissionWithHierarchy(perms, permission);
      }
    }
  }

  if (binding.scopeType === RoleBindingScopeType.ORGANIZATION) {
    if (binding.role === TeamUserRole.CUSTOM) return false;
    if (resolution.organizationRole === OrganizationUserRole.EXTERNAL) {
      return false;
    }
    if (binding.role === TeamUserRole.ADMIN) return true;
    return organizationRoleHasPermission(
      OrganizationUserRole.MEMBER,
      permission,
    );
  }

  if (resolution.organizationRole === OrganizationUserRole.EXTERNAL) {
    return hasPermissionWithHierarchy(EXTERNAL_MEMBER_PERMISSIONS, permission);
  }
  return teamRoleHasPermission(binding.role, permission);
}

/** Does the caller hold `permission` on this project, given a loaded resolution? */
function projectGrants(
  resolution: ScopeResolution,
  args: { organizationId: string; projectId: string; teamId?: string },
  permission: Permission,
): boolean {
  const grantedBy = (key: string) =>
    (resolution.bindingsByScope.get(key) ?? []).some((b) =>
      bindingGrants(resolution, b, permission),
    );

  if (
    grantedBy(scopeKey(RoleBindingScopeType.ORGANIZATION, args.organizationId))
  ) {
    return true;
  }
  if (grantedBy(scopeKey(RoleBindingScopeType.PROJECT, args.projectId))) {
    return true;
  }
  if (args.teamId) {
    if (grantedBy(scopeKey(RoleBindingScopeType.TEAM, args.teamId))) {
      return true;
    }
    if (resolution.needsLegacyFallback) {
      const legacy = resolution.legacyByTeam.get(args.teamId);
      if (legacy) {
        return bindingGrants(
          resolution,
          {
            role: legacy.role,
            customRoleId: legacy.assignedRoleId,
            scopeType: RoleBindingScopeType.TEAM,
          },
          permission,
        );
      }
    }
  }
  return false;
}

/** Does the caller hold `permission` on this team, given a loaded resolution? */
function teamGrants(
  resolution: ScopeResolution,
  args: { organizationId: string; teamId: string },
  permission: Permission,
): boolean {
  const grantedBy = (key: string) =>
    (resolution.bindingsByScope.get(key) ?? []).some((b) =>
      bindingGrants(resolution, b, permission),
    );

  if (
    grantedBy(scopeKey(RoleBindingScopeType.ORGANIZATION, args.organizationId))
  ) {
    return true;
  }
  if (grantedBy(scopeKey(RoleBindingScopeType.TEAM, args.teamId))) return true;
  if (resolution.needsLegacyFallback) {
    const legacy = resolution.legacyByTeam.get(args.teamId);
    if (legacy) {
      return bindingGrants(
        resolution,
        {
          role: legacy.role,
          customRoleId: legacy.assignedRoleId,
          scopeType: RoleBindingScopeType.TEAM,
        },
        permission,
      );
    }
  }
  return false;
}

/**
 * MANY permissions, ONE project scope — resolved in ~4 queries total.
 *
 * The counterpart axis to `batchScopePermissions` (one permission, many scopes),
 * and the fix for the pattern that function's own docstring warns about: a caller
 * that needs to know which of N permissions a user holds must NOT issue N scoped
 * checks. Langy's session-key mint did exactly that — 27 candidate permissions at
 * ~3 queries each, ~81 queries per chat turn. Serially that was ~500ms of latency;
 * fanned out with Promise.all it became ~81 connections demanded at once, which
 * starved the Prisma pool and made `ApiKeyService.create`'s interactive
 * transaction exceed its 5-second budget and abort — turning a slow turn into a
 * failed one.
 *
 * Returns the held subset, in the order given, so callers can use it directly as
 * a least-privilege grant list.
 */
/**
 * @deprecated Not for new callers. Same ADR-110 fork seam as the single
 * checks above, and it goes away with the legacy walk. New code batches
 * through `getApp().permissions` , which reaches
 * the engine's `canBatchByIds` directly.
 */
export async function batchProjectPermissions(
  ctx: { prisma: PrismaClient; session: Session | null },
  args: {
    organizationId: string;
    projectId: string;
    teamId?: string;
    permissions: Permission[];
  },
): Promise<Permission[]> {
  const resolution = await loadScopeResolution(ctx, {
    organizationId: args.organizationId,
    scopeIds: [args.projectId, ...(args.teamId ? [args.teamId] : [])],
  });
  if (!resolution) return [];

  return args.permissions.filter((permission) =>
    projectGrants(
      resolution,
      {
        organizationId: args.organizationId,
        projectId: args.projectId,
        ...(args.teamId ? { teamId: args.teamId } : {}),
      },
      permission,
    ),
  );
}

/**
 * MANY permissions, MANY team scopes — the team-scope counterpart of
 * `batchProjectPermissions`, resolved with one `loadScopeResolution` round
 * for the whole team set.
 *
 * Exists for the CLI login-key default selection: the minted key carries one
 * permission list asserted at every stamped team scope, so the default list
 * has to be narrowed to what the user really holds per team BEFORE the mint
 * asserts it — and asking per permission per team is the pool-starving
 * fan-out `batchProjectPermissions` documents. Like that function, this is
 * the legacy resolution on purpose: it feeds a mint whose ceiling is
 * re-asserted (and forked on cut-over organizations) by
 * `ApiKeyService.create`, so a divergence can only narrow the result, never
 * widen it.
 *
 * Returns the held subset per team, in the order given.
 */
/**
 * @deprecated Not for new callers. Same ADR-110 fork seam as the single
 * checks above, and it goes away with the legacy walk. New code batches
 * through `getApp().permissions` , which reaches
 * the engine's `canBatchByIds` directly.
 */
export async function batchTeamsPermissions(
  ctx: { prisma: PrismaClient; session: Session | null },
  args: {
    organizationId: string;
    teamIds: string[];
    permissions: Permission[];
  },
): Promise<Map<string, Permission[]>> {
  const result = new Map<string, Permission[]>();
  const resolution = await loadScopeResolution(ctx, {
    organizationId: args.organizationId,
    scopeIds: args.teamIds,
  });
  for (const teamId of args.teamIds) {
    if (!resolution) {
      result.set(teamId, []);
      continue;
    }
    result.set(
      teamId,
      args.permissions.filter((permission) =>
        teamGrants(
          resolution,
          { organizationId: args.organizationId, teamId },
          permission,
        ),
      ),
    );
  }
  return result;
}

/**
 * The legacy batch resolution — the answering path in `batchScopePermissions`
 * when the organization is still on legacy. (It was also the fork's
 * reverse-shadow thunk before the shadow comparison was removed; the engine
 * now answers a migrated organization outright.) It builds its own maps per
 * call, so no two callers can hand out the same mutable pair.
 */
async function legacyBatchScopePermissions(
  ctx: { prisma: PrismaClient; session: Session | null },
  args: {
    organizationId: string;
    teamIds: string[];
    projectIds: string[];
    projectTeamId: Record<string, string>;
    permission: Permission;
  },
): Promise<{ teams: Map<string, boolean>; projects: Map<string, boolean> }> {
  const teamsMap = new Map<string, boolean>();
  const projectsMap = new Map<string, boolean>();

  // A project inherits TEAM-scoped bindings from its team, so the binding
  // load must cover the projects' team ids too — `projectGrants` below
  // checks `scopeKey(TEAM, teamId)`, and a binding that was never loaded
  // can't grant. Without this, a member whose only access is a TEAM-scope
  // binding (no legacy TeamUser rows) fails every project in the batch
  // while the single-project resolver — which always queries the team
  // scope — grants it.
  const resolution = await loadScopeResolution(ctx, {
    organizationId: args.organizationId,
    scopeIds: [
      ...new Set([
        ...args.teamIds,
        ...args.projectIds,
        ...Object.values(args.projectTeamId),
      ]),
    ],
  });
  if (!resolution) {
    args.teamIds.forEach((id) => teamsMap.set(id, false));
    args.projectIds.forEach((id) => projectsMap.set(id, false));
    return { teams: teamsMap, projects: projectsMap };
  }

  for (const teamId of args.teamIds) {
    teamsMap.set(
      teamId,
      teamGrants(
        resolution,
        { organizationId: args.organizationId, teamId },
        args.permission,
      ),
    );
  }

  for (const projectId of args.projectIds) {
    const teamId = args.projectTeamId[projectId];
    projectsMap.set(
      projectId,
      projectGrants(
        resolution,
        {
          organizationId: args.organizationId,
          projectId,
          ...(teamId ? { teamId } : {}),
        },
        args.permission,
      ),
    );
  }

  return { teams: teamsMap, projects: projectsMap };
}

/**
 * Batched team + project permission check used by surfaces that need to
 * test the SAME permission across many scopes inside one organization
 * (e.g. the model-defaults settings page enumerating every team +
 * project the caller can read/write). One scoped permission check costs
 * ~3-5 queries (team/project lookup, organizationUser, groupMembership,
 * roleBinding, optional customRole). N team + M project checks ran in a
 * Promise.all fan-out, that's hundreds of queries per page load on large
 * orgs.
 *
 * This helper does the four lookups ONCE — via `loadScopeResolution` — then
 * resolves each id in-memory against the same rules a per-call check applies.
 *
 * Project resolution still needs to know the project's team so a
 * team-scoped binding inherits to its projects. Callers pass the
 * project→teamId map alongside the project ids.
 */
/**
 * @deprecated Not for new callers. Same ADR-110 fork seam as the single
 * checks above, and it goes away with the legacy walk. New code batches
 * through `getApp().permissions` , which reaches
 * the engine's `canBatchByIds` directly.
 */
export async function batchScopePermissions(
  ctx: { prisma: PrismaClient; session: Session | null },
  args: {
    organizationId: string;
    teamIds: string[];
    projectIds: string[];
    projectTeamId: Record<string, string>;
    permission: Permission;
  },
): Promise<{ teams: Map<string, boolean>; projects: Map<string, boolean> }> {
  const legacyBatch = () => legacyBatchScopePermissions(ctx, args);

  const userId = ctx.session?.user?.id;

  // One collected snapshot answers every scope in the batch. Deciding per
  // scope would fan the legacy batch's four flat queries into a collect per
  // scope — the pool-starving fan-out api-key.service.ts documents.
  if (
    userId &&
    (await organizationOnAuthzEngine({
      prisma: ctx.prisma,
      organizationId: args.organizationId,
    }))
  ) {
    const decision = await authzChecksFor(ctx.prisma).canBatchByIds({
      principal: { type: "user", id: userId },
      permission: args.permission,
      organizationId: args.organizationId,
      teams: args.teamIds.map((teamId) => ({ teamId })),
      projects: args.projectIds.map((projectId) => ({
        projectId,
        teamId: args.projectTeamId[projectId],
      })),
    });
    return { teams: decision.teams, projects: decision.projects };
  }

  const { teams: teamsMap, projects: projectsMap } = await legacyBatch();

  return { teams: teamsMap, projects: projectsMap };
}

// ============================================================================
// DEMO PROJECT HANDLING
// ============================================================================

const DEMO_VIEW_PERMISSIONS: Permission[] = [
  "project:view",
  "analytics:view",
  "navigation:view",
  "cost:view",
  "traces:view",
  "annotations:view",
  "datasets:view",
  "evaluations:view",
  "tasks:view",
  "qaPolicy:view",
  "workflows:view",
  "experiments:view",
  "prompts:view",
  "scenarios:view",
  "playground:view",
];

/**
 * Whether `projectId` is the public demo project. The demo grants
 * `DEMO_VIEW_PERMISSIONS` to every authenticated user (see `isDemoProject`), so a
 * surface that must NOT be reachable there at all — e.g. the per-user Langy chat,
 * whose conversations belong to whoever actually used Langy on the demo — gates
 * on this directly instead of on the permission check that the demo silently
 * grants.
 */
export function isDemoProjectId(projectId: string | null | undefined): boolean {
  if (!projectId) return false;
  // Prefer dynamic process.env in tests; fall back to validated env.
  const demoId = process.env.DEMO_PROJECT_ID ?? env.DEMO_PROJECT_ID;
  return !!demoId && projectId === demoId;
}

export function isDemoProject(
  projectId: string,
  permission: Permission,
): boolean {
  return (
    isDemoProjectId(projectId) && DEMO_VIEW_PERMISSIONS.includes(permission)
  );
}

/**
 * For procedures that authorize against data-dependent scopes resolved at
 * runtime (e.g. a row's own scope set, loaded by id) rather than a fixed
 * input scope a declared `.permission()` could read. It satisfies the
 * builder's fail-closed `enforcePermissionCheck` while keeping
 * `protectedProcedure`'s auth + audit + domain-error handling. The
 * resolver/service MUST perform the real authorization — this only defers
 * WHERE the check happens, never whether it happens.
 *
 * Declared generically for the sweep; new procedures prefer
 * `.authorizeInService({ reason, permissions })`, which names what the
 * service enforces at the call site.
 */
/**
 * For procedures whose authorization is data-dependent and runs inside the
 * resolver (a membership filter, a scope loaded from the row being acted
 * on). The declaration must CLAIM, per scope field the input carries, what
 * enforces it — the sweep counts a claimed field as covered and fails on an
 * unclaimed one, so an input growing a new scope id turns CI red until the
 * resolver's enforcement is named. The claims are prose, but they are
 * per-field and reviewable; the predecessor of this factory claimed nothing
 * and stamped every input covered, which is how 23 endpoints went unchecked.
 */
export function authorizeInResolver(enforces: EnforcedScopeFields) {
  return declareAuthzMiddleware(
    {
      kind: "service-authorized",
      reason:
        "the scope is data the resolver loads at runtime; the resolver enforces each claimed field (see `enforces`)",
      permissions: [],
      enforces,
    },
    ({ ctx, next }: PermissionMiddlewareParams<object>) => {
      ctx.permissionChecked = true;
      return next();
    },
  );
}

// ============================================================================
// OPS PERMISSION
// ============================================================================

/**
 * Discriminated scope every authenticated user has — `none` means "no ops
 * access" (the honest answer for a non-ops user), `platform` means "full
 * platform-wide access". Modeled this way so `getScope` can be a status
 * probe that returns data instead of throwing FORBIDDEN on every page
 * load (lw#3584).
 */
export type OpsScope = { kind: "none" } | { kind: "platform" };

/**
 * Resolve the ops scope for a user. Always returns a typed scope value;
 * non-ops users get `{ kind: "none" }` instead of null. Shared between
 * tRPC middleware (which still rejects non-ops callers via
 * `checkOpsPermission`) and the SSE endpoint.
 *
 * Only users listed in ADMIN_EMAILS get the `platform` scope. All ops
 * data is platform-wide so no org-scoped tier exists.
 *
 * Impersonation: an impersonation session rewrites `session.user` to the
 * impersonated (customer) identity, which would hide the ops UI (Deja
 * View) exactly when an admin impersonates a customer to debug their
 * account. The impersonator's own grant carries through instead — the
 * scope is resolved against the real admin's email as well.
 */
export function resolveOpsScope({
  userEmail,
  impersonatorEmail,
}: {
  userId: string;
  userEmail: string | null | undefined;
  /** Email of the real admin behind an impersonation session, if any. */
  impersonatorEmail?: string | null;
  permission: Permission;
  prisma: unknown;
}): OpsScope {
  if (isAdmin({ email: userEmail }) || isAdmin({ email: impersonatorEmail })) {
    return { kind: "platform" };
  }

  return { kind: "none" };
}

export const checkOpsPermission = ({
  permission,
  throwOnDeny = true,
}: {
  permission: Permission;
  throwOnDeny?: boolean;
}) =>
  declareAuthzMiddleware(
    {
      kind: "custom",
      reason:
        "platform-tier operator check: resolves the admin allow-list into an ops scope no procedure input carries",
      permissions: [permission],
    },
    async ({ ctx, next }: PermissionMiddlewareParams<unknown>) => {
      const user = ctx.session?.user;
      if (!user) {
        throw new TRPCError({ code: "UNAUTHORIZED" });
      }

      const opsScope = await resolveOpsScope({
        userId: user.id,
        userEmail: user.email,
        impersonatorEmail: user.impersonator?.email,
        permission,
        prisma: ctx.prisma,
      });

      // For mutating endpoints, `kind: "none"` is a hard FORBIDDEN. For status
      // probes that want to *report* "no access" without throwing (lw#3584
      // — see ops.getScope), pass `{ throwOnDeny: false }` so the middleware
      // populates `ctx.opsScope = { kind: "none" }` and the procedure handler
      // can branch on it.
      if (opsScope.kind === "none" && throwOnDeny) {
        throw new TRPCError({
          code: "FORBIDDEN",
          message: "You do not have permission to access ops resources",
        });
      }

      ctx.opsScope = opsScope;
      ctx.permissionChecked = true;
      return next();
    },
  );
